import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-enterprise';
import { ReportService } from '../..';
import { ConstantService } from '@app/_services/constant.service';
import { Router } from '@angular/router';
import 'ag-grid-enterprise';

@Component({
    selector: 'app-upsell-dashboard',
    templateUrl: './upsell-dashboard.component.html',
    styleUrls: ['./upsell-dashboard.component.css'],
    standalone: false
})
export class UpsellDashboardComponent implements OnChanges {

    @Input() upsellOffers: any[] = [];

    gridApi: GridApi;
    columnApi: ColumnApi;

    constructor(
        private router: Router,
        public reportService: ReportService,
        private constantService: ConstantService,
    ) { }

    defaultColDef: ColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        minWidth: 80,
    };

    columnDefs: ColDef[] = [
        {
            headerName: 'Status',
            field: 'offerStatus',
            minWidth: 100,
            maxWidth: 120,
            cellRenderer: (params: any) => this.getStatusBadge(params),
            filterValueGetter: (params: any) => params.data?.offerStatus,
        },
        {
            headerName: 'Property',
            field: 'propertyCode',
            sortable: true,
            sort: 'asc',
        },
        {
            headerName: 'Development',
            field: 'propertyName',
            sortable: true,
        },
        {
            headerName: 'Guest',
            field: 'guestLastName',
            cellRenderer: (params: any) => this.getReservationLink(params),
            valueGetter: (params: any) => {
                const last  = params?.data?.guestLastName  || '';
                const first = params?.data?.guestFirstName || '';
                return last ? (first ? last + ', ' + first : last) : '—';
            },
        },
        {
            headerName: 'Sale Date',
            field: 'saleDate',
            headerTooltip: 'When the reservation was originally booked',
            cellRenderer: this.fullDateTimeCellRenderer,
        },
        {
            headerName: 'Check-In',
            field: 'checkInDate',
            cellRenderer: this.fullDateCellRenderer,
        },
        {
            headerName: 'Check-Out',
            field: 'checkOutDate',
            cellRenderer: this.fullDateCellRenderer,
        },
        {
            headerName: 'Offer Date',
            field: 'offerDate',
            headerTooltip: 'The extra night being offered',
            cellRenderer: this.fullDateCellRenderer,
        },
        {
            headerName: 'Direction',
            field: 'direction',
            minWidth: 90,
            maxWidth: 110,
            headerTooltip: 'NEXT = stay one extra night after checkout · PREV = arrive one night earlier',
            cellRenderer: (params: any) => {
                if (!params.value) return '—';
                if (params.value === 'NEXT') {
                    return '<span style="background:#fff3cd;color:#856404;padding:2px 7px;border-radius:4px;font-size:12px;font-weight:600;">&#x2192; NEXT</span>';
                }
                return '<span style="background:#cce5ff;color:#004085;padding:2px 7px;border-radius:4px;font-size:12px;font-weight:600;">&#x2190; PREV</span>';
            },
        },
        {
            headerName: 'Nights',
            field: 'originalNights',
            maxWidth: 80,
            headerTooltip: 'Original → Extended nights',
            cellRenderer: (params: any) => {
                const orig = params.data?.originalNights;
                return orig != null ? orig + ' → ' + (orig + 1) : '—';
            },
        },
        {
            headerName: 'Discount',
            field: 'effectivePct',
            maxWidth: 90,
            headerTooltip: 'Effective discount % (post owner-floor cap)',
            cellRenderer: (params: any) => {
                if (params.value == null) return '—';
                const raw = params.data?.discountPct;
                const eff = params.value;
                const capped = raw != null && raw !== eff
                    ? `<span title="Capped from ${raw}%">${eff}%*</span>`
                    : `${eff}%`;
                return `<b style="color:#856404;">${capped}</b>`;
            },
        },
        {
            headerName: 'Rate',
            field: 'nightlyRateWithFeesAndTax',
            headerTooltip: 'Per-night room rate + Resort/Destination fees + tax (pre-coupon)',
            aggFunc: 'avg',
            cellRenderer: (params: any) => params.data ? this.currencyCellRendererUSD(params) : '',
        },
        {
            headerName: 'Extra Night $',
            field: 'extraNightDiscounted',
            headerTooltip: 'Discounted extra-night charge (existing reservation rate with promo applied)',
            aggFunc: 'sum',
            cellRenderer: (params: any) => params.data ? this.currencyCellRendererUSD(params) : '',
        },
        {
            headerName: 'Source',
            field: 'referralSourceId',
            maxWidth: 75,
            cellRenderer: (params: any) => this.displayChannelPicture(params),
            filterValueGetter: (params: any) => {
                const refSrcId = params.data?.referralSourceId;
                const channel = this.constantService.refSrcImages.find((item: any) => item.value === refSrcId);
                return channel ? channel.label : refSrcId;
            },
        },
        {
            headerName: 'SMS Sent',
            field: 'smsMsgId',
            minWidth: 85,
            maxWidth: 90,
            headerTooltip: 'SMS was sent to the guest (all rows in this table were created via the SMS pathway)',
            cellRenderer: (params: any) => {
                if (!params.data) return '';
                // Every row in this table exists because an SMS was sent —
                // persistNewOffer() is only called when smsSentNext || smsSentPrev.
                // smsMsgId may be null when Twilio doesn't return a message ID,
                // but that does not mean the SMS was not sent.
                const tooltip = params.data.smsMsgId ? `SMS ID: ${params.data.smsMsgId}` : 'SMS sent (no ID returned by provider)';
                return `<span style="color:#27ae60;" title="${tooltip}">&#x2713; Sent</span>`;
            },
        },
        {
            headerName: 'Offer Sent',
            field: 'createdDate',
            headerTooltip: 'When the promotion offer / upsell SMS was generated',
            cellRenderer: this.fullDateTimeCellRenderer,
        },
        {
            headerName: 'Applied',
            field: 'appliedDate',
            headerTooltip: 'When the reservationist applied the offer',
            cellRenderer: this.fullDateTimeCellRenderer,
        },
        {
            headerName: 'Charge',
            field: 'chargeApproved',
            headerTooltip: 'CC charge result at apply time',
            minWidth: 140,
            cellRenderer: (params: any) => this.getChargeRenderer(params),
        },
        {
            headerName: 'Res #',
            field: 'reservationId',
            hide: true,
        },
        {
            headerName: 'Order #',
            field: 'orderId',
            hide: true,
        },
    ];

    gridOptions: GridOptions = {
        columnDefs: this.columnDefs,
        pagination: true,
        paginationPageSize: 50,
        rowSelection: 'single',
        suppressAggFuncInHeader: true,
        tooltipShowDelay: 100,
        tooltipHideDelay: 2147483647,
        domLayout: 'autoHeight',

        getRowHeight: () => 25,

        getRowStyle: (params: any) => {
            if (!params.data) return {};
            const status = params.data.offerStatus;
            if (status === 'APPLIED')  return { background: '#f0f8ff' };
            if (status === 'DECLINED') return { background: '#fff5f5' };
            if (status === 'EXPIRED')  return { background: '#f8f8f8', color: '#999' };
            return {};
        },

        onGridReady: (event: any) => {
            this.gridApi    = event.api;
            this.columnApi  = event.columnApi;
        },

        getContextMenuItems: (params: any) => {
            const items: any[] = [];
            if (params.node?.data?.orderId) {
                items.push({
                    name: 'Open Order in New Tab',
                    action: () => {
                        window.open(`https://admin.kbmhawaii.com/order/${params.node.data.orderId}`, '_blank');
                    },
                });
            }
            if (params.node?.data?.shortTermRentalId) {
                items.push({
                    name: 'View Property Reservations',
                    action: () => {
                        window.open(`https://admin.kbmhawaii.com/reservations/property/${params.node.data.propertyCode}`, '_blank');
                    },
                });
            }
            items.push('separator');
            items.push(...(params.defaultItems || []));
            return items;
        },
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['upsellOffers'] && this.gridApi) {
            this.gridApi.setRowData(this.upsellOffers || []);
            setTimeout(() => this.columnApi?.autoSizeAllColumns(), 300);
        }
    }

    onGridReady(event: any): void {
        this.gridApi   = event.api;
        this.columnApi = event.columnApi;
    }

    onFirstDataRendered(params: any): void {
        setTimeout(() => params.columnApi?.autoSizeAllColumns(), 300);
    }

    onRowGroupOpened(event: any): void {
        setTimeout(() => this.columnApi?.autoSizeAllColumns(), 100);
    }

    // ---- Cell renderers ----

    getStatusBadge(params: any): string {
        const status = params.value;
        const map: Record<string, { bg: string; color: string }> = {
            'OFFERED':   { bg: '#fff3cd', color: '#856404' },
            'ACCEPTED':  { bg: '#d4edda', color: '#155724' },
            'APPLIED':   { bg: '#cce5ff', color: '#004085' },
            'DECLINED':  { bg: '#f8d7da', color: '#721c24' },
            'EXPIRED':   { bg: '#e2e3e5', color: '#383d41' },
            'CANCELLED': { bg: '#e2e3e5', color: '#383d41' },
        };
        const c = map[status] || { bg: '#e2e3e5', color: '#383d41' };
        return `<span style="background:${c.bg};color:${c.color};padding:2px 8px;border-radius:10px;font-size:11px;font-weight:bold;">${status || '—'}</span>`;
    }

    getReservationLink(params: any): string {
        const name = params.value;
        if (!name || name === '—') return '—';
        const orderId = params?.data?.orderId;
        if (!orderId) return name;
        const url = `https://admin.kbmhawaii.com/order/${orderId}`;
        return `<a href="${url}" target="_blank">${name}</a>`;
    }

    getChargeRenderer(params: any): string {
        const d = params.data;
        if (!d) return '';
        if (d.chargeAttempted === true && d.chargeApproved === true) {
            const amt  = d.chargeAmount != null ? '$' + d.chargeAmount.toFixed(2) : '';
            const mask = d.chargeCardMask ? ' · ' + d.chargeCardMask : '';
            return `<span style="color:#27ae60;">✓ ${amt}${mask}</span>`;
        }
        if (d.chargeAttempted === true && d.chargeApproved === false) {
            return `<span style="color:#c0392b;" title="${d.chargeErrorMessage || ''}">✗ Declined</span>`;
        }
        if (d.chargeAttempted === false) {
            return `<span style="color:#856404;" title="${d.chargeErrorMessage || ''}">Manual</span>`;
        }
        return '<span style="color:#aaa;">—</span>';
    }

    displayChannelPicture(params: any): string {
        const refSrcId = params?.data?.referralSourceId;
        if (!refSrcId) return '—';
        const channel = this.constantService.refSrcImages.find((item: any) => item.value === refSrcId);
        if (channel?.img) {
            return `<img src="${channel.img}" style="height:18px;width:auto;" title="${channel.label}" />`;
        }
        return channel?.label || String(refSrcId);
    }

    fullDateCellRenderer(params: any): string {
        if (!params.value) return '—';
        try {
            const d = new Date(params.value);
            return isNaN(d.getTime()) ? params.value : (d.getMonth() + 1) + '/' + d.getDate() + '/' + String(d.getFullYear()).slice(-2);
        } catch { return params.value; }
    }

    fullDateTimeCellRenderer(params: any): string {
        if (!params.value) return '—';
        try {
            const d = new Date(params.value);
            if (isNaN(d.getTime())) return params.value;
            const date = (d.getMonth() + 1) + '/' + d.getDate() + '/' + String(d.getFullYear()).slice(-2);
            const hours = d.getHours() % 12 || 12;
            const mins  = String(d.getMinutes()).padStart(2, '0');
            const ampm  = d.getHours() >= 12 ? 'pm' : 'am';
            return `${date} ${hours}:${mins}${ampm}`;
        } catch { return params.value; }
    }

    currencyCellRendererUSD(params: any): string {
        if (params.value == null) return '—';
        return '$' + Number(params.value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
}
